<template>
<div class="rate-list-history">
    <div class="row p-b-lg">
        <div class="col-xs-3">
            <label>Filter by Date Range:</label>
            <date-range-picker
                :start-date="filterDateRange.start"
                :end-date="filterDateRange.end"
                v-on:update="updateDate"
            ></date-range-picker>
        </div>
        <div class="col-xs-3">
            <label>Filter by Market:</label>
            <multiselect
                v-if="marketData.length > 0"
                v-model="filterMarkets"
                :options="marketData"
                :multiple="true"
                placeholder="--"
                :close-on-select="false"
                group-values="markets"
                group-label="name"
                :group-select="true"
                :searchable="true"
                track-by="id"
                label="name"
                :disabled="loading"
                @close="handleMarketSelectionChanges"
                @remove="handleMarketSelectionChanges"
                >
                </multiselect>
            <p v-else>Loading markets...</p>
        </div>
        <div class="col-xs-3 col-xs-offset-3">
            <br />
            <label v-if="loading">Please wait while loading...</label>
            <label v-else>{{filteredRates.length}} rate update(s) displayed</label>
        </div>
    </div>
    <div class="row p-b-lg">
        <div class="col-xs-3">
            <label>Filter by Advertiser:</label>
            <multiselect
                v-if="advertisers.length > 0"
                v-model="filterAdvertiser"
                :options="advertisers"
                :multiple="false"
                track-by="id"
                placeholder="--"
                :close-on-select="true"
                :searchable="true"
                label="name"
                :disabled="loading"
                >
                </multiselect>
            <p v-else>Loading advertisers...</p>
        </div>
        <div class="col-xs-3">
            <label>Filter by Type:</label>
            <select v-model="filterType" class="form-control" :disabled="loading">
                <option value="">--</option>
                <option
                    v-for="(typeName, typeKey) in types"
                    v-bind:value="typeKey"
                    v-bind:key="typeKey"
                    >{{typeName}}</option>
            </select>
        </div>
        <div class="col-xs-3">
            <br />
            <label>
                <input type="checkbox" :checked="filterOutQcedItems" @change="toggleFilterOutQcedItems" />
                Filter out Already QC'ed Items
            </label>
        </div>
        <div class="col-xs-3">
            <template v-if="filtering">
                <br />
                <a href="#" @click="clearFilter">Clear Filter</a>
            </template>
        </div>
    </div>
    <table class="table table-striped rate-history-table">
        <thead>
            <tr>
                <th></th>
                <th>Advertiser</th>
                <th>Type</th>
                <th>Market Group</th>
                <th>Exp Date</th>
                <th>Intro<br />Rate</th>
                <th>Rate</th>
                <th>Loan Amount<br />Min</th>
                <th>Loan Amount<br />Max</th>
                <th>Fees</th>
                <th>Draw<br />Amount</th>
                <th>Term</th>
                <th>Fico<br />Min</th>
                <th>Fico<br />Max</th>
                <th>LTV<br />Min</th>
                <th>LTV<br />Max</th>
                <th>Date Recorded</th>
                <th></th>
            </tr>
        </thead>
        <template v-if="rates.length > 0">
            <tbody v-for="rate in filteredRates" v-bind:key="rate.id+'_body'" v-bind:class="{'history-body': true, 'history-body-qc': rate.qc_time !== null}">
                <rate-history-row v-bind:rate="rate">
                    <template slot="prepend-cells">
                        <td>
                            <input type="checkbox" @change="clickQCCheckbox" v-bind:value="rate.id" v-if="rate.qc_time === null" />
                            <input type="checkbox" checked="checked" disabled v-else />
                        </td>
                        <td>{{ getAdvertiserName(rate.advertiser_id) }}</td>
                    </template>
                </rate-history-row>
                <rate-history-text-row v-bind:rate="rate">
                    <template slot="prepend-cells">
                        <td colspan="2">
                            <button v-if="rate.properties.old" @click="handleTogglePreviousVersion(rate)" class="btn-link">
                                <span :class="{'glyphicon': true, 'glyphicon-chevron-right': !(rate.previousVersion), 'glyphicon-chevron-down': !!(rate.previousVersion)}"></span>
                            </button>
                        </td>
                    </template>
                </rate-history-text-row>
                <rate-history-qc-row v-if="rate.qc_time != null" v-bind:qc-time="rate.qc_time" v-bind:qc-user="rate.qc_user">
                    <template slot="prepend-cells">
                        <td colspan="2"></td>
                    </template>
                </rate-history-qc-row>
                <!-- begin list of previous version data -->
                <rate-history-row v-if="rate.previousVersion" v-bind:rate="rate.previousVersion" class="previous-version">
                    <template slot="prepend-cells">
                        <td colspan="2"></td>
                    </template>
                </rate-history-row>
                <rate-history-text-row v-if="rate.previousVersion" v-bind:rate="rate.previousVersion" class="previous-version">
                    <template slot="prepend-cells">
                        <td colspan="2"></td>
                    </template>
                </rate-history-text-row>
            </tbody>
            <tbody v-if="filteredRates.length == 0">
                <tr>
                    <td colspan="17">No Rates match the given filter.  <a href="#" @click="clearFilter">Click here to clear the filter</a>.</td>
                </tr>
            </tbody>
        </template>
        <tbody v-else>
            <tr>
                <td colspan="17">
                    <template v-if="filterMarkets.length == 0">
                        No history for the selected date range.
                    </template>
                    <template v-else>
                        No history for the selected date range &amp; markets.
                    </template>
                </td>
            </tr>
        </tbody>
    </table>
</div>
</template>

<style lang="scss">
    .rate-list-history {
        .dateRangePicker-input.form-control {
            background: #ffffff;
        }
        .table.rate-history-table {
            > {
                tbody.history-body {
                    border-bottom:2px solid #aaa;
                }
                tbody:nth-of-type(even) > tr {
                    background-color: #ffffff;
                }
                tbody:nth-of-type(odd) > tr {
                    background-color: #f7f7f7;
                }
                tbody + tbody {
                    border-top: 0;
                }
            }
            tbody {
                .rate-history-row {
                    .field-updated {
                        background-color: #ffcccc;
                    }
                    td + td {
                        border-top-color: #eee;
                    }

                    &.previous-version {
                        td {
                            opacity: 0.6;
                        }
                    }
                }

                .rate-history-row-qc {
                    font-size: 0.9em;
                    font-style: italic;
                    color: #666666;
                }

                &.history-body-qc {
                    tr {
                        td:first-child {
                            border-left: 2px solid #337ab7;
                        }
                    }
                }
            }
            .text-muted {
                color: #aaa;
            }
        }
    }
</style>

<script type="text/babel">
import Multiselect from 'vue-multiselect'
import RateHistoryRow from './rate-list-history-row.vue'
import RateHistoryTextRow from './rate-list-history-text-row.vue'
import RateHistoryQcRow from './rate-list-history-qc-row.vue'

// used in the filteredRates function in place of other filter evaluators, to reduce number of calls to Array.prototype.filter
const filterNoOp = () => {
    return true;
}

export default {
    props: ['getRateHistoryUrl', 'getAdvertisersUrl', 'getStateMarketsUrl', 'qualityControlUrl'],
    data() {
        return {
            rates: [],                  // all rates loaded from backend.
            advertisers: [],            // list that populates the advertiser filter
            marketData: [],             // list that populates the market filter
            advertiserNameById: {},     // dictionary used to display advertiser for each rate.  use this to avoid having to query this.advertisers N number of times.
            // values that populate the product type filter.
            types: {
                'HELOC': 'HELOC',
                'Home-Equity-Loan': 'Home Equity Loan',
                'Home-Co-Investing': 'Home Co-Investing',
                'Cashout-Refinance': 'Cash Out Refinance'
            },
            loadingRates: false,        // indicator of whether we're loading rates from the backend
            loadingAdvertisers: false,  // indicator of whether we're loading advertisers from the backend
            loadingMarkets: false,    // indicator of whether we're loading states & markets from the backend
            filterType: '',             // value of currently selected product type filter
            filterAdvertiser: null,     // value of currently selected advertiser filter
            filterOutQcedItems: false,  // whether to hide items that have been qc'ed
            // date range selected by filter.
            filterDateRange: {
                // "7 days ago" is the default for this filter, but for some reason we have to subtract 6.
                start: moment().subtract(6, 'd'),
                end: moment()
            },
            filterMarkets: [],          // list of rate markets to filter on.
            throttledFetch: null,
        }
    },
    computed: {
        filteredRates() {
            let rates = this.rates;
            let typeFilterFunc = filterNoOp;
            let advertiserFilterFunc = filterNoOp;
            let qcFilterFunc = filterNoOp;

            if (this.filterType) {
                typeFilterFunc = (rate) => { return rate.type == this.filterType; }
            }
            if (this.filterAdvertiser && this.filterAdvertiser.id) {
                advertiserFilterFunc = (rate) => { return rate.advertiser_id == this.filterAdvertiser.id; }
            }
            if (this.filterOutQcedItems) {
                qcFilterFunc = (rate) => { return rate.qc_time === null }
            }

            if (this.filtering) {
                rates = rates.filter((rate) => {
                    return typeFilterFunc(rate) && advertiserFilterFunc(rate) && qcFilterFunc(rate);
                });
            }

            return rates;
        },
        loading () {
            return this.loadingRates || this.loadingAdvertisers || this.loadingMarkets;
        },
        filtering () {
            return !!(this.filterType || this.filterAdvertiser || this.filterOutQcedItems);
        }
    },
    methods: {
        getAdvertiserName (advertiserId) {
            if (this.advertiserNameById[advertiserId]) {
                return this.advertiserNameById[advertiserId];
            }
            return '--';
        },
        fetchRateHistory() {
            this.loadingRates = true;
            let marketIds = this.filterMarkets.reduce((carry, market) => {
                carry.push(market.id);
                return carry;
            }, []);
            axios.get(this.getRateHistoryUrl, {
                    params: {
                        startDate: this.filterDateRange.start.format('YYYY-MM-DD'),
                        endDate: this.filterDateRange.end.format('YYYY-MM-DD'),
                        marketIds: marketIds,
                    }
                })
                .then((response) => {
                    this.loadingRates = false;
                    this.rates = response.data.rates;
                })
                .catch(() => {
                    this.loadingRates = false;
                });
        },
        handleTogglePreviousVersion(rate) {
            if (rate.previousVersion) {
                this.rates = this.rates.map((mapRate) => {
                    if (rate.id == mapRate.id) {
                        mapRate.previousVersion = false;
                    }
                    return mapRate;
                });
                return;
            }
            axios.get(this.getRateHistoryUrl, {
                    params: {
                        subjectId: rate.subject_id,
                        beforeLogId: rate.log_id,
                        limit: 1,
                    }
                })
                .then((response) => {
                    if (response.data.rates.length > 0) {
                        this.rates = this.rates.map((mapRate) => {
                            if (rate.id == mapRate.id) {
                                mapRate.previousVersion = response.data.rates[0];
                            }
                            return mapRate;
                        });
                    }
                })
        },
        fetchAdvertisers() {
            this.loadingAdvertisers = true;
            axios.get(this.getAdvertisersUrl)
                .then((response) => {
                    this.advertisers = response.data.advertisers;
                    // populate the advertiserNameById
                    this.advertisers.forEach(advertiser => {
                        this.advertiserNameById[advertiser.id] = advertiser.name;
                    });
                    this.loadingAdvertisers = false;
                })
                .catch(() => {
                    this.loadingAdvertisers = false;
                });
        },
        fetchStateMarkets() {
            this.loadingMarkets = true;
            axios.get(this.getStateMarketsUrl)
                .then((response) => {
                    this.marketData = response.data.states;
                    this.loadingMarkets = false;
                })
                .catch(() => {
                    this.loadingMarkets = false;
                });
        },
        clearFilter(e) {
            e.preventDefault();

            this.filterType = '';
            this.filterAdvertiser = null;
            this.filterOutQcedItems = false;
        },
        updateDate(dateStart, dateEnd) {
            this.filterDateRange = {
                start: moment(dateStart),
                end: moment(dateEnd)
            };

            this.fetchRateHistory();
        },
        handleMarketSelectionChanges() {
            if (this.throttledFetch == null) {
                //set up the throttle.  we need to do it here because of how _.throttle works, and how js does `this`
                this.throttledFetch = _.throttle(() => {
                    this.fetchRateHistory();
                }, 400, { 'leading': false });
            }

            this.throttledFetch();
        },
        toggleFilterOutQcedItems(e) {
            this.filterOutQcedItems = !this.filterOutQcedItems;
        },
        clickQCCheckbox (e) {
            if (!e.target.checked) {    // only do any action if the checkbox has been checked.
                return;
            }
            e.target.disabled = true;
            axios.post(this.qualityControlUrl, { id: e.target.value })
                .then((response) => {
                    let updatedRate = response.data.rate;
                    // replace the updated rate with the new data associated with that rate so that the interface updates appropriately.
                    this.rates = this.rates.map((rate) => {
                        if (rate.id == updatedRate.id) {
                            return updatedRate;
                        }
                        return rate;
                    });
                }).catch((err) => {
                    e.target.disabled = false;
                    e.target.checked = false;
                });
        },
    },
    mounted() {
        this.fetchRateHistory();
        this.fetchAdvertisers();
        this.fetchStateMarkets();
    },
    components: {
        Multiselect,
        RateHistoryRow,
        RateHistoryTextRow,
        RateHistoryQcRow,
    }
}
</script>
